import React, { useState, useRef, useEffect, useMemo } from "react";
import { useAtom } from "jotai";
import ProgressLine from "../progressBar";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import "./styles.scss";
import { IconChevronsLeft, IconDotsVertical } from "@tabler/icons-react";
import { componentDTO } from "../../constants/statusDTO";
import { ReactComponent as LoadIcon } from "../../assets/segment_building.svg";
import { ReactComponent as BatteryIcon } from "../../assets/battery2_general.svg";
import { ReactComponent as GridIcon } from "../../assets/segment_energy_infrastructure.svg";
import { ReactComponent as PVIcon } from "../../assets/solar_panel1.svg";
import { ReactComponent as GensetIcon } from "../../assets/diesel_generator.svg";
import { ReactComponent as ControllerIcon } from "../../assets/controller.svg";

import { addedStepsAtom } from "../../utils/atoms/addedStepsAtom";
import { ProgressSkeleton } from "../Skeleton";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import { Button, useMantineColorScheme } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { SeIcon } from "@se/web-ui-react";
import AlertIcon from "../Alert";
import { sideMenu } from "../../utils/atoms/sideMenuAtom";
import { tourAtom } from "../../utils/atoms/tourAtom";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import SEConfirmModal from "../common/SEConfirmModal";
import { capitalizeFirstLetterOnly, useCurrentDesignComponentLists, useCurrentDesignVersion } from "../../constants/helper";
import { saveDisabledAtom } from "../../utils/atoms/saveDisabledAtom";
import { getComponentsByType } from "../../utils/atoms/componentFilterUtils";

const EvcsIconFixed = ({ fill, className }) => (
  <se-icon size="large" style={{ color: fill }} className={className}>electric_vehicle_terminal</se-icon>
);

const ComponentProgressBar = ({
  stepProgress,
  addedSteps,
  handleComponentClick,
  setShowNextSteps,
  showNextSteps,
  activeComponent,
  triggerLoadMethod,
  setAddedSteps,
  setActiveComponent,
  updateProgressComponent,
  setSnackBar,
  snackBarRef,
  lastComponent,
  loading,
  onOk,
  loadingComp,
}) => {
    const { t } = useTranslation();
    const [userScope] = useAtom(userScopeAtom);
  const IconSVGs = {
    load: LoadIcon,
    grid: GridIcon,
    pv: PVIcon,
    battery: BatteryIcon,
    genset: GensetIcon,
    controller: ControllerIcon,
    evcs: EvcsIconFixed
  };
  const [sideMenuShow] = useAtom(sideMenu);
  const [projectComponents, setProjectComponents] = useAtom(componentsAtom);
  const [, setAddedStepsSeries] = useAtom(addedStepsAtom);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const [componentStatus, setComponentStatus] = useAtom(componentStatusAtom);
  const [componentDetails, ] = useAtom(componentsAtom);
  const [tour] = useAtom(tourAtom);
  const [saveDisabled] = useAtom(saveDisabledAtom);
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(projectDetails?.designComponentsList);
  const selectedVersion = useCurrentDesignVersion(projectDetails?.designWithVersionsDTOList);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    step: null,
  });
  const [stepToRemove, setStepToRemove] = useState(null); // State to track the step to be removed
  const [alert, setAlert] = useState({
    message: "",
    alertPopUpStatus: false,
    title: "",
  });
  const [showPvDeleteWarning, setShowPvDeleteWarning] = useState(false);

  const saveComponents = () => {
    triggerLoadMethod();
  };
  const removeStep = (step) => {
    const updatedSteps = addedSteps.filter((s) => s !== step);
    setAddedSteps(updatedSteps);
    setAddedStepsSeries(updatedSteps);
    setActiveComponent(addedSteps[updatedSteps?.length - 1]);
    updateProgressComponent(step.toLowerCase());
    setComponentStatus((prev) => ({
      ...prev,
      [step]: {
        ...prev[step],
        completed: false,
        isEditing: false,
      },
    }));
    // setProjectComponents((prev) => ({
    //   ...prev,
    //   [step]: componentsAtom?.[step],
    // }));
    closeAlertMessage();
    setSnackBar({
      type: "success",
      message: t("progressBar.componentRemoved", { component:step["step"]==='battery' ? "BESS" :step["step"] === "controller" ? "Controls" : step["step"] === "evcs" ? "EVCS" : capitalizeFirstLetterOnly(step) }),
    });
    snackBarRef.current.open = true;
  };

  React.useEffect(() => {
    const handleResize = () => {
      if (contextMenu.visible) {
        setContextMenu((prev) => ({
          ...prev,
          visible: false,
        }));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [contextMenu.visible]);
  const handleContextMenuOpen = (e, step, index) => {
    e.preventDefault();
    e.stopPropagation();
    setStepToRemove({
      step,
      index,
    });

    setContextMenu({
      visible: true,
      x: e.clientX * 1.17,
      y: e.clientY * 1.17 + 20,
      step,
    });
  };
  const handleContextMenuClose = () => {
    setContextMenu({ visible: false, x: 0, y: 0, step: null });
  };

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenu.visible) {
        const contextMenuElement = document.querySelector(
          ".context-menu-remove"
        );
        if (contextMenuElement && !contextMenuElement.contains(e.target)) {
          setContextMenu({ visible: false, x: 0, y: 0, step: null });
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu.visible]);

  const handleRemoveClickWithConfirmation = (e, step) => {
    handleContextMenuClose();
    setShowPvDeleteWarning(false);
    setAlert((prev) => ({
      ...prev,
      message: t("progressBar.removeConfirmation", { component: step["step"] === "battery" ? "BESS" : step["step"] === "controller" ? "Controls" : step["step"] === "evcs" ? "EVCS" : step["step"] }),
      title: t("progressBar.removeComponentTitle"),
      alertPopUpStatus: true,
    }));
  };
  const closeAlertMessage = () => {
    setShowPvDeleteWarning(false);
    setAlert((prev) => ({
      ...prev,
      alertPopUpStatus: false,
    }));
  };
    const filteredComponentDTOList = useMemo(() => {
      return selectedDesignAndDesignVersion.componentDTOList.filter(
        (component) => component.componentType.toLowerCase() !== "thermalload"
      );
    }, [selectedDesignAndDesignVersion.componentDTOList]);
  const checkForSteps = () => {
    if (selectedDesignAndDesignVersion?.componentDTOList?.length === 0) {
      setSnackBar({
        type: "error",
        message: t("progressBar.loadSaveFirst"),
      });
      snackBarRef.current.open = true;
      return false;
    }
    if (
      activeComponent === componentDTO?.[lastComponent?.componentType] ||
      addedSteps.length === filteredComponentDTOList.length
    ) {
      return true;
    }
    setSnackBar({
      type: "error",
      message: t("progressBar.saveToContinue", { component: addedSteps?.slice(-1)?.[0]?.toUpperCase()=== "BATTERY" ? "BESS" :addedSteps?.slice(-1)?.[0]?.toUpperCase()=== "CONTROLLER" ? "Controls" : addedSteps?.slice(-1)?.[0]?.toUpperCase()=== "EVCS" ? "EVCS" : addedSteps?.slice(-1)?.[0]?.toUpperCase() }),
    });
    snackBarRef.current.open = true;
    return false;
  };

  const onSubmit = async (step) => {
    const isComponent = selectedDesignAndDesignVersion?.componentDTOList?.filter(
      (item) => componentDTO[item?.componentType?.toLowerCase()] === step?.step
    );
    const pvCount =
      getComponentsByType(
        selectedDesignAndDesignVersion?.countComponentDTOList,
        "PV",
      ) || 0;
    if (step?.step === "pv" && pvCount?.length > 1 && !showPvDeleteWarning) {
      setShowPvDeleteWarning(true);
      setAlert((prev) => ({
        ...prev,
        message: t("progressBar.multiplePvDeleteWarning"),
        title: t("progressBar.removeComponentTitle"),
        alertPopUpStatus: true,
      }));
      return;
    }

    if (isComponent?.length > 0) {
      const isDeleted = await onOk(step);
      if (isDeleted) {
        setShowPvDeleteWarning(false);
        setAlert((prev) => ({
          ...prev,
          alertPopUpStatus: false,
        }));
        setSnackBar({
          type: "success",
          message: t("progressBar.deletedSuccessfully", { component:step["step"] === "battery" ? "BESS" : step["step"] === "controller" ? "Controls" : step["step"] === "evcs" ? "EVCS" : capitalizeFirstLetterOnly(step["step"]) }),
        });
        snackBarRef.current.open = true;
      }
    } else {
      setShowPvDeleteWarning(false);
      removeStep(step["step"]);
    }
  };

  const { colorScheme } = useMantineColorScheme();
  const isLightMode = colorScheme === "light";

  // Sequential reveal state
  const [revealedCount, setRevealedCount] = useState(0);
  const revealTimerRef = useRef(null);
  const prevLengthRef = useRef(0);

  // Sequential reveal for additional (showNextSteps) list
  const [nextRevealCount, setNextRevealCount] = useState(0);
  const nextRevealTimerRef = useRef(null);
  const [displayNextSteps, setDisplayNextSteps] = useState(showNextSteps);
  const [closingNext, setClosingNext] = useState(false);
  // Flag to skip closing animation when adding a component (so panel disappears instantly)
  const skipClosingAnimationRef = useRef(false);
  // Start / extend sequential reveal whenever steps list grows or on first mount
  useEffect(() => {
    const target = addedSteps.length; // includes 'load' if present
    // If list shrank, reset reveal to new length
    if (target < revealedCount) {
      setRevealedCount(target);
    }
    // If list grew, incrementally reveal the new ones
    if (target > revealedCount) {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
      const tick = () => {
        setRevealedCount(prev => {
          if (prev >= target) return prev;
          return prev + 1;
        });
        revealTimerRef.current = setTimeout(() => {
          setRevealedCount(prev => (prev >= target ? prev : prev + 1));
          if (revealedCount + 2 <= target) tick();
        }, 120);
      };
      // Prime first increment (if we are at 0, show first immediately for snappier UX)
      if (revealedCount === 0) {
        setRevealedCount(1);
      }
      revealTimerRef.current = setTimeout(tick, 120);
    }
    prevLengthRef.current = target;
    return () => { if (revealTimerRef.current) clearTimeout(revealTimerRef.current); };
  }, [addedSteps, revealedCount]);

  // Helper to assign sequence classes
  const getSeqClass = (seqIndex) => {
    return seqIndex < revealedCount ? "seq-visible" : "seq-hidden";
  };

  const getNextSeqClass = (seqIndex) => {
    return seqIndex < nextRevealCount ? "seq-visible" : "seq-hidden";
  };

  // Effect to animate additional steps open/close with sequential transitions
  useEffect(() => {
    const availableSteps = Object.keys(stepProgress || {})
      .filter(step => !addedSteps.includes(step) && step.toLowerCase() !== 'load');

    if (showNextSteps) {
      // Opening
      setClosingNext(false);
      setDisplayNextSteps(true);
      if (nextRevealTimerRef.current) clearTimeout(nextRevealTimerRef.current);
      const target = availableSteps.length;
      setNextRevealCount(0);
      if (target === 0) return;
      const stepDelay = 90;
      const reveal = (i) => {
        setNextRevealCount(i);
        if (i < target) {
          nextRevealTimerRef.current = setTimeout(() => reveal(i + 1), stepDelay);
        }
      };
      nextRevealTimerRef.current = setTimeout(() => reveal(1), stepDelay);
    } else {
      // Closing (sequential hide)
      if (!displayNextSteps) return; // already removed
      if (skipClosingAnimationRef.current) {
        // Instant close path
        if (nextRevealTimerRef.current) clearTimeout(nextRevealTimerRef.current);
        setNextRevealCount(0);
        setDisplayNextSteps(false);
        setClosingNext(false);
        skipClosingAnimationRef.current = false; // reset flag
      } else {
        setClosingNext(true);
        if (nextRevealTimerRef.current) clearTimeout(nextRevealTimerRef.current);
        const start = nextRevealCount || availableSteps.length;
        const stepDelay = 70;
        const hide = (i) => {
          setNextRevealCount(i);
          if (i > 0) {
            nextRevealTimerRef.current = setTimeout(() => hide(i - 1), stepDelay);
          } else {
            setDisplayNextSteps(false);
            setClosingNext(false);
          }
        };
        hide(start);
      }
    }
    return () => { if (nextRevealTimerRef.current) clearTimeout(nextRevealTimerRef.current); };
  }, [showNextSteps, stepProgress, addedSteps]);

  const handleToggleNextSteps = () => {
    if (checkForSteps()) setShowNextSteps(!showNextSteps);
  };

  // Immediately ensure Next Steps is closed when tour is on
  useEffect(() => {
    if (tour && showNextSteps) {
      handleToggleNextSteps();
    }
  }, [tour]);

  // Close the additional steps panel on any click ONLY during tour
  useEffect(() => {
    if (!tour) return; // attach listener only when tour active
    const onGlobalClick = () => {
      if (showNextSteps) {
        handleToggleNextSteps();
      }
    };
    // Use capture phase to ensure we catch clicks even if overlays stop propagation
    document.addEventListener('mousedown', onGlobalClick, { capture: true });
    document.addEventListener('touchstart', onGlobalClick, { capture: true });
    document.addEventListener('click', onGlobalClick, { capture: true });
    return () => {
      document.removeEventListener('mousedown', onGlobalClick, { capture: true });
      document.removeEventListener('touchstart', onGlobalClick, { capture: true });
      document.removeEventListener('click', onGlobalClick, { capture: true });
    };
  }, [showNextSteps, tour]);

  return loading ? (
    <ProgressSkeleton />
  ) : (
    <div className="progress-bar-scroll-wrapper">
      <>
        <AlertIcon
          alert={alert}
          onClose={closeAlertMessage}
          onSubmit={showPvDeleteWarning ? null : () => onSubmit(stepToRemove)}
        />
        {/* <div
          ref={stickyWrapperRef}
          style={{
            position: "sticky", // default; may be toggled to relative during active tour highlight
            top: "0px",
            zIndex: "10",
            minHeight: "165px",
            backgroundColor:
              colorScheme === "dark"
                ? "var(--se-background-alternative)"
                : "#ffffff",
          }}
        > */}
        <div
          className="fixedPgressBar"
          style={{
            display: "flex",
            backgroundColor:
              colorScheme === "dark"
                ? "var(--se-background-alternative)"
                : "white",
            justifyContent: "space-between",
            boxShadow: "0px 4px 4px rgba(90, 116, 148, 0.3)",
            position: "relative",
            width: "calc(100% - 0px)",
          }}
        >
          <div
            style={{ display: "flex", minHeight: "100px", alignItems: "center" }}
          >
            <div className="timer-check">
              <se-icon
                size="small"
                aria-label={componentStatus["load"]?.completed ? "load-component-complete" : "load-component-pending"}
                style={{
                  marginRight: "-5px",
                  color: "#3dcd58",
                }}
              >
                {componentStatus["load"]?.completed
                  ? "notification_ok_stroke"
                  : "timer"}
              </se-icon>
              <span>&nbsp;</span>
            </div>

            <div
              id="iconl-plus-progressbar"
              style={{ display: "flex", alignItems: "center" }}
              className={getSeqClass(0)}
            >
                <div className="progress-items"
                  data-intro={t("progressBar.loadIntro")}
                  data-position="right"
                  >
                <div
                  onClick={() => handleComponentClick("load")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleComponentClick("load");
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className={`step-wrapper ${
                    activeComponent === "load" ? "active-step" : ""
                  }`}
                  style={{
                    backgroundColor:
                      colorScheme === "dark"
                        ? "var(--se-background-alternative)"
                        : "#f1f3f5",
                    border:
                      activeComponent === "load"
                        ? "revert-layer"
                        : colorScheme === "dark"
                        ? " 0.125rem solid #505559"
                        : " 0.125rem solid rgb(241, 243, 245)",
                  }}
                >
                  <LoadIcon
                    width={"50"}
                    height={"50"}
                    fill={"#3dcd58"}
                    className="icon-hover"
                  />
                </div>
                <span
                  style={{
                    color: "#3dcd58",
                  }}
                  className="loadTitle"
                >
                  load
                </span>
              </div>
              <div style={{ marginBottom: "3px"}}>
                <ProgressLine
                  orientation="horizontal"
                  color="#3dcd58"
                  thickness={2.5}
                  length={sideMenuShow?.showMenu ? 5 : 7}
                  dashed={addedSteps.length === 1 ? true : false}
                  borderColorData={
                    colorScheme === "dark"
                      ? "var(--se-background-alternative)"
                      : "#fff"
                  }
                  position="relative"
                />
              </div>
              <span>&nbsp;</span>
            </div>
            {addedSteps
              .filter((step) => step !== "load")
              .map((step, index) => {
                const IconSetMapping = IconSVGs[step];
                const seqIndex = addedSteps.indexOf(step); // position in full ordered list
                return (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    className={getSeqClass(seqIndex)}
                  >
                    <se-icon
                      size="small"
                      aria-label={`${step}-component-${componentStatus?.[step]?.completed && !componentStatus?.[step]?.isEditing ? "complete" : "pending"}`}
                      style={{
                        color: "#3dcd58",
                      }}
                    >
                      {componentStatus?.[step]?.completed &&
                      !componentStatus?.[step]?.isEditing
                        ? "notification_ok_stroke"
                        : "timer"}
                    </se-icon>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <div
                        id="iconb-plus-progress"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <div
                          className={`progress-items ${
                            activeComponent === step.toLowerCase()
                              ? "selected-progress-items"
                              : ""
                          }`}
                            data-intro={t("progressBar.componentConfigure", { step: step?.toUpperCase?.() })}
                            data-position="bottom"
                        >
                          <div
                            onClick={() => handleComponentClick(step)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleComponentClick(step);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            className={`step-wrapper ${
                              activeComponent === step.toLowerCase()
                                ? "active-step"
                                : ""
                            }`}
                            style={{
                              backgroundColor:
                                colorScheme === "dark"
                                  ? "var(--se-background-alternative)"
                                  : "#f1f3f5",
                              border:
                                activeComponent === step.toLowerCase()
                                  ? "revert-layer"
                                  : colorScheme === "dark"
                                  ? " 0.125rem solid #505559"
                                  : " 0.125rem solid rgb(241, 243, 245)",
                              cursor: "pointer",
                            }}
                          >
                            <IconSetMapping
                              width={"50"}
                              height={"50"}
                              fill={"#3dcd58"}
                              className="icon-hover"
                            />
                            {
                              userScope?.access?.deleteComponent && (selectedVersion?.versionStatus !== "COMPLETED" && selectedVersion?.versionStatus !== "SIMULATED") && (
                                <IconDotsVertical
                                  color={"#505559"}
                                  className={
                                    colorScheme === "dark"
                                      ? "context-icon"
                                      : "context-icon-light"
                                  }
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleContextMenuOpen(e, step, index);
                                    }
                                  }}
                                  onClick={(e) =>
                                    handleContextMenuOpen(e, step, index)
                                  }
                                />
                              )
                            }
                          </div>
                          <span
                            style={{
                              color: "#3dcd58",
                            }}
                            className="loadTitle"
                          >
                            {step === "pv" || step === "Pv" ? "PV" : step === "battery" ? "BESS" : step === "controller" ? "Controls" : step === "evcs" ? "EVCS" : step}
                          </span>
                        </div>
                        {addedSteps.length === 7 && index === 5 ? null : (
                          <div style={{ marginBottom: "3px" }}>
                            <ProgressLine
                              orientation="horizontal"
                              color="#3dcd58"
                              thickness={2.5}
                              length={sideMenuShow?.showMenu ? 5 : 7}
                              dashed={
                                addedSteps?.slice?.(1)?.length - 1 === index
                                  ? true
                                  : false
                              }
                              borderColorData={
                                colorScheme === "dark"
                                  ? "var(--se-background-alternative)"
                                  : "#fff"
                              }
                              position="relative"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            {addedSteps.length !== 7 &&
              (!displayNextSteps ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    pointerEvents:
                      selectedVersion?.versionStatus === "COMPLETED" ||
                      !userScope?.access?.saveUpdateComponent 
                        ? "none"
                        : "",
                        opacity:
                          selectedVersion?.versionStatus === "COMPLETED" ||
                          !userScope?.access?.saveUpdateComponent
                            ? 0.5
                            : 1,
                  }}
                  data-intro={t("progressBar.addAnotherComponent")}
                  data-position="bottom"
                  
                >
                  {<se-icon
                    style={{
                      color: "#3dcd58",
                      cursor: "pointer",
                    }}
                    size="medium"
                    aria-label="add-another-component"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleToggleNextSteps();
                      }
                    }}
                    onClick={handleToggleNextSteps}
                  >
                    action_add_flat
                  </se-icon>}
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  data-intro={t("progressBar.addAnotherComponent")}
                  data-position="bottom"
                >
                  <IconChevronsLeft
                    size={32}
                    style={{
                      color: "#3dcd58",
                      cursor: "pointer",
                      width: 32,
                      height: 32,
                      display: "flex",
                    }}
                    aria-label="Hide additional components"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleToggleNextSteps();
                      }
                    }}
                    onClick={handleToggleNextSteps}
                  />
                </div>
              ))}
            <div>
              {displayNextSteps && (
                <span
                  className={`additional-icons ${(showNextSteps && !closingNext) ? "show" : closingNext ? "closing" : ""}`}
                  style={{
                    position: "relative",
                    display: "inline-flex",
                  }}
                >
                  {Object.keys(stepProgress)
                    .filter(
                      (step) =>
                        !addedSteps.includes(step) &&
                        step.toLowerCase() !== "load"
                    )
                    .map((step, index) => {
                      const IconComponent = IconSVGs[step];
                      return (
                        <span
                          key={index}
                          style={{
                            display: "flex",
                            cursor: "pointer",
                            alignItems: "center",
                          }}
                          className={getNextSeqClass(index)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (showNextSteps) {
                                skipClosingAnimationRef.current = true;
                                setShowNextSteps(false);
                              }
                              setTimeout(() => handleComponentClick(step, true), 0);
                            }
                          }}
                          onClick={() => {
                            // Immediately close the additional steps panel BEFORE adding component
                            if (showNextSteps) {
                              skipClosingAnimationRef.current = true;
                              setShowNextSteps(false);
                            }
                            // Defer component addition to next tick so UI can render closed state first
                            setTimeout(
                              () => handleComponentClick(step, true),
                              0
                            );
                          }}
                        >
                          <div
                            className={`progress-items ${
                              activeComponent === step.toLowerCase()
                                ? "selected-progress-items"
                                : ""
                            }`}
                            style={{ position: "relative" }}
                          >
                            <div
                              className="step-wrapper"
                              style={{
                                backgroundColor:
                                  colorScheme === "dark"
                                    ? "var(--se-background-alternative)"
                                    : "#f1f3f5s",
                                border:
                                  colorScheme === "dark"
                                    ? " 0.125rem solid #505559"
                                    : " 0.125rem solid rgb(241, 243, 245)",
                              }}
                              data-intro={t("progressBar.clickToAddComponent", { step: step?.toUpperCase?.() })}
                              data-position="bottom"
                            >
                              <IconComponent
                                fill="#42B4E6"
                                width={"50"}
                                height={"50"}
                                className="icon-hover"
                              />
                            </div>

                            <span
                              className={`loadTitle ${
                                step.toLowerCase().includes("controller") ||
                                step.toLowerCase().includes("grid")
                                  ? "mandatory-component1"
                                  : ""
                              }`}
                            >
                              {step === "pv" || step === "Pv" ? "PV" : step === "battery" ? "BESS" :step === "controller" ? "Controls" : step === "evcs" ? "EVCS" : step}
                            </span>
                          </div>
                        </span>
                      );
                    })}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: selectedVersion?.versionStatus === "COMPLETED" || selectedVersion?.versionStatus === "SIMULATED" ? "column" : "row", alignItems: "center", padding: "15px" }}>
            {selectedVersion?.versionStatus === "COMPLETED" || selectedVersion?.versionStatus === "SIMULATED" ? (
             <>
               <div
                 style={{
                   width: "100%",
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                  //  cursor: "not-allowed",
                  //  opacity: 0.7,
                   position: "relative",
                 }}
               >
                   <Button
                   aria-label="progress-bar-save-readonly"
                   loading={loadingComp}
                   color="seGreen.0"
                   onClick={() => setShowCompletedModal(true)}
                   style={{
                     color: !isLightMode ? "#2e3032" : "#ffffff",
                    //  pointerEvents: "none",
                   }}
                   
                 >
                   <SeIcon aria-hidden="true">action_save</SeIcon>
                   &nbsp;{t("common.save")}
                 </Button>
               </div>
               <SEConfirmModal
                 opened={showCompletedModal}
                 onClose={() => setShowCompletedModal(false)}
                 onlyOk
                 header={t("progressBar.versionCompleted")}
                 message={t("progressBar.editingRestricted")}
                 confirmLabel={t("common.ok")}
               />
             </>
            ) : (
                <Button
                aria-label="progress-bar-save"
                loading={loadingComp}
                color="seGreen.0"
                onClick={saveComponents}
                style={{
                  color: !isLightMode ? "#2e3032" : "#ffffff",
                }}
                data-intro={t("tour.saveComponentConfiguration")}
                data-position="left"
                id="save-progressbar-btn"
                disabled={saveDisabled ? saveDisabled : !userScope?.access?.saveUpdateComponent}
              >
                <SeIcon aria-hidden="true">action_save</SeIcon>
                &nbsp;{t("common.save")}
              </Button>
          )}
            {(selectedVersion?.versionStatus === "COMPLETED" || selectedVersion?.versionStatus === "SIMULATED") && (
              <div
                style={{
                  fontSize: "12px",
                  marginTop: "6px",
                  textAlign: "center",
                  fontFamily: "Nunito, sans-serif",
                  width: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "break-word",
                }}
              >
              {selectedVersion?.versionStatus === "SIMULATED" ? t("progressBar.simulationInProgress") : t("progressBar.editingRestrictedShort")}
              </div>
            )}
          </div>
        </div>
        {/* </div> */}
        {contextMenu.visible && (
          <div
            className="context-menu-remove"
            style={{
              position: "fixed",
              top: contextMenu.y,
              left: contextMenu.x,
              boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.2)",
              borderRadius: "4px",
              // zIndex: 10000,
              padding: "0px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f0f0f0";
              e.currentTarget.style.borderRadius = "5px";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "white";
            }}
          >
            <Button
              aria-label={`progress-bar-remove-step-${stepToRemove || "none"}`}
              variant="filled"
              color={"gray"}
              style={{
                cursor: "pointer",
              }}
              onClick={(e) => handleRemoveClickWithConfirmation(e, stepToRemove)}
            >
              {t("common.remove")}
            </Button>
          </div>
        )}
      </>
    </div>
  );
};

export default ComponentProgressBar;
